import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Menu, X } from "lucide-react";
import { useCart } from "react-use-cart";
import { useAuth } from "../context/AuthContext";

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const toggleCartDropdown = () => setIsCartOpen((prev) => !prev);
  const closeCartDropdown = () => setIsCartOpen(false);

  const { items } = useCart();
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const { logout } = useAuth();
  const storedUser = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuColor = isScrolled ? "text-[#7a6e5f]" : "text-[#7a6e5f]";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white shadow-md" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-16 items-center">
          <Link
            to="/"
            className="text-2xl font-serif font-bold transition-colors text-[#809080]"
          >
            SuprSkin
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {["/", "/products", "/about", "/contact", "/articles"].map(
              (path, index) => {
                const label = ["Home", "Products", "About", "Contact", "Articles"][index];
                return (
                  <Link
                    key={path}
                    to={path}
                    className={`hover:opacity-75 transition-colors ${menuColor}`}
                  >
                    {label}
                  </Link>
                );
              }
            )}
            {storedUser ? (
              <button
                onClick={logout}
                className={`hover:opacity-75 transition-colors ${menuColor}`}
              >
                Logout
              </button>
            ) : (
              <Link
                to="/login"
                className={`hover:opacity-75 transition-colors ${menuColor}`}
              >
                Login
              </Link>
            )}

            {/* Cart Dropdown */}
            <div className="relative">
              <button
                onClick={toggleCartDropdown}
                className="relative focus:outline-none"
              >
                <ShoppingCart className={`w-6 h-6 ${menuColor}`} />
                {totalItems > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                    {totalItems}
                  </span>
                )}
              </button>

              {isCartOpen && (
                <div className="absolute right-0 mt-3 w-80 bg-white shadow-lg rounded-md border border-[#f5eee6] z-50">
                  <div className="max-h-60 overflow-y-auto p-4 space-y-4">
                    {items.length > 0 ? (
                      items.map((item) => (
                        <div key={item.id} className="flex items-center gap-4">
                          <img
                            src={
                              item.image
                                ? `${process.env.REACT_APP_API_URL}/storage/${item.image}`
                                : "https://via.placeholder.com/40x40"
                            }
                            alt={item.name}
                            className="w-10 h-10 object-cover rounded"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-[#7a6e5f]">
                              {item.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {item.quantity} × Rp{item.price.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-sm text-[#7a6e5f] py-4">
                        Your cart is empty
                        <div className="mt-2">
                          <Link
                            to="/products"
                            className="text-[#809080] underline text-sm"
                          >
                            Shop now
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>

                  {items.length > 0 && (
                    <div className="border-t px-4 py-3 flex justify-between gap-2">
                      <Link
                        to="/cart"
                        className="text-sm text-[#7a6e5f] hover:underline"
                        onClick={closeCartDropdown}
                      >
                        View Cart
                      </Link>
                      <Link
                        to="/checkout"
                        className="text-sm bg-[#809080] text-white px-3 py-1.5 rounded hover:bg-[#6d7d6d]"
                        onClick={closeCartDropdown}
                      >
                        Checkout
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={menuColor}
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden pb-4 bg-[#fef8f5]">
            <div className="flex flex-col space-y-4 text-[#7a6e5f]">
              <Link to="/">Home</Link>
              <Link to="/products">Products</Link>
              <Link to="/about">About</Link>
              <Link to="/contact">Contact</Link>
              <Link to="/articles">Articles</Link>
              <Link to="/login">Login</Link>
              <Link to="/cart">Cart ({totalItems})</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

